import type { AttachmentBlockModel } from '@blocksuite/affine/model';
import { useEffect, useState } from 'react';

import type { AttachmentViewerSource } from '../types';

const PDF_VIEWPORT_SCROLL_QUANTUM = 16;

export type AttachmentPdfCanvasPresentationState = {
  pageIndex: number;
  scrollLeft: number;
  scrollTop: number;
  updatedAt: number;
};

export type PdfFullscreenViewportState =
  AttachmentPdfCanvasPresentationState & {
    zoom: number;
    consumedRequestId?: string;
  };

export function createPdfFullscreenStorageKey(
  source: AttachmentBlockModel | AttachmentViewerSource
) {
  if ('props' in source) {
    return [
      'streamlain',
      'pdf-fullscreen',
      source.store.id,
      source.id,
      source.props.sourceId ?? 'pending',
    ].join(':');
  }
  return [
    'streamlain',
    'pdf-fullscreen',
    source.docId,
    source.id,
    source.blobId ?? 'pending',
  ].join(':');
}

export function readPdfFullscreenViewportState({
  key,
  maxPageIndex,
  maxZoom,
  minZoom,
}: {
  key: string;
  maxPageIndex: number;
  maxZoom: number;
  minZoom: number;
}): PdfFullscreenViewportState | null {
  try {
    const value = window.localStorage.getItem(key);
    if (!value) return null;
    const parsed = JSON.parse(value) as Partial<PdfFullscreenViewportState>;
    const pageIndex = clampPageIndex(parsed.pageIndex, maxPageIndex);
    const zoom = clampNumber(parsed.zoom, minZoom, maxZoom, 1);
    return {
      pageIndex,
      zoom,
      scrollLeft: clampScroll(parsed.scrollLeft),
      scrollTop: clampScroll(parsed.scrollTop),
      updatedAt: clampNumber(parsed.updatedAt, 0, Number.MAX_SAFE_INTEGER, 0),
      consumedRequestId:
        typeof parsed.consumedRequestId === 'string'
          ? parsed.consumedRequestId
          : undefined,
    };
  } catch {
    return null;
  }
}

export function writePdfFullscreenViewportState({
  key,
  state,
}: {
  key: string;
  state: PdfFullscreenViewportState;
}) {
  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // localStorage can be unavailable in private contexts.
  }
}

export function getAttachmentPdfCanvasPresentation(
  model: AttachmentBlockModel
) {
  const props = model.props as unknown as {
    pdfCanvasPresentation?: AttachmentPdfCanvasPresentationState;
  };
  return normalizePdfCanvasPresentation(props.pdfCanvasPresentation);
}

export function useAttachmentPdfCanvasPresentation(
  model: AttachmentBlockModel
) {
  // The state is read when the block changes, not on every render: a reader that
  // follows it must see a new value only when someone turned the page. A fresh
  // object per render made the embedded card take the stored page back after
  // every local turn.
  const [presentation, setPresentation] = useState(() =>
    getAttachmentPdfCanvasPresentation(model)
  );

  useEffect(() => {
    setPresentation(current => {
      const next = getAttachmentPdfCanvasPresentation(model);
      return isSamePdfCanvasPresentation(current, next) ? current : next;
    });
    const subscription = model.store.slots.blockUpdated.subscribe(payload => {
      if (payload.type !== 'update') return;
      if (payload.id !== model.id) return;
      if (payload.props.key !== 'pdfCanvasPresentation') return;
      setPresentation(getAttachmentPdfCanvasPresentation(model));
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [model]);

  return presentation;
}

function isSamePdfCanvasPresentation(
  a: AttachmentPdfCanvasPresentationState | null,
  b: AttachmentPdfCanvasPresentationState | null
) {
  if (!a || !b) return a === b;
  return (
    a.pageIndex === b.pageIndex &&
    a.scrollLeft === b.scrollLeft &&
    a.scrollTop === b.scrollTop &&
    a.updatedAt === b.updatedAt
  );
}

export function updateAttachmentPdfCanvasPresentation({
  model,
  state,
}: {
  model: AttachmentBlockModel;
  state: AttachmentPdfCanvasPresentationState;
}) {
  const next = normalizePdfCanvasPresentation(state);
  if (!next) return;
  const current = getAttachmentPdfCanvasPresentation(model);
  if (
    current &&
    current.pageIndex === next.pageIndex &&
    Math.abs(current.scrollLeft - next.scrollLeft) <= 2 &&
    Math.abs(current.scrollTop - next.scrollTop) <= 2
  ) {
    return;
  }

  model.store.updateBlock(model, {
    pdfCanvasPresentation: next,
  } as Partial<AttachmentBlockModel['props']>);
}

export function createPdfCanvasPresentationState({
  pageIndex,
  scrollLeft,
  scrollTop,
  updatedAt = Date.now(),
}: {
  pageIndex: number;
  scrollLeft: number;
  scrollTop: number;
  updatedAt?: number;
}): AttachmentPdfCanvasPresentationState {
  return {
    pageIndex: clampPageIndex(pageIndex, Number.MAX_SAFE_INTEGER),
    scrollLeft: quantizeScroll(scrollLeft),
    scrollTop: quantizeScroll(scrollTop),
    updatedAt,
  };
}

export function quantizeScroll(value: number | undefined) {
  return (
    Math.round(clampScroll(value) / PDF_VIEWPORT_SCROLL_QUANTUM) *
    PDF_VIEWPORT_SCROLL_QUANTUM
  );
}

export function clampPageIndex(value: unknown, maxPageIndex: number) {
  const numericValue = typeof value === 'number' ? value : 0;
  if (!Number.isFinite(numericValue)) return 0;
  return Math.max(0, Math.min(Math.floor(numericValue), maxPageIndex));
}

function normalizePdfCanvasPresentation(
  value: unknown
): AttachmentPdfCanvasPresentationState | null {
  if (!value || typeof value !== 'object') return null;
  const state = value as Partial<AttachmentPdfCanvasPresentationState>;
  return createPdfCanvasPresentationState({
    pageIndex: clampPageIndex(state.pageIndex, Number.MAX_SAFE_INTEGER),
    scrollLeft: state.scrollLeft ?? 0,
    scrollTop: state.scrollTop ?? 0,
    updatedAt: clampNumber(state.updatedAt, 0, Number.MAX_SAFE_INTEGER, 0),
  });
}

function clampScroll(value: unknown) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.max(0, value);
}

function clampNumber(
  value: unknown,
  min: number,
  max: number,
  fallback: number
) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}
